import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";

import DataTableComponent from "../common/DataTableComponent";
import UserColumn from "../common/UserColumn";
import PermissionsGate from "../../utils/permissionGate";
import hasPermission from "../../utils/hasMultiplePermission";
import { TABLE_DATE_FORMAT } from "../../utils/Constants";
import { initFilter } from "../../utils/globals";
import { RenderBrands, RenderUserRole } from "../../utils/CommonGroupingItem";
import {
  formatReportRolesForExport,
  formatReportUsersForExport,
  mapReportUsersForColumn,
  ReportRolesCell,
  RenderReportCategory,
  RenderReportStatus,
  REPORT_USER_COLUMN_FIELDS,
} from "./ReportHelpers";
import {
  deleteReport,
  getReportList,
  getReportListFilter,
} from "../../services/reportService";
import SkeletonTicketList from "../../loaders/SkeletonTicketList";

const getReportListRows = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  return responseData?.data || responseData?.rows || [];
};

const getReportListTotal = (responseData) => {
  if (Array.isArray(responseData)) return responseData.length;
  return responseData?.total || getReportListRows(responseData).length || 0;
};

/**
 * Paginated reports list with filters, export, and row actions.
 * @returns {JSX.Element} Reports data table.
 */
const ListOfReports = () => {
  const givenPermissions = useSelector((state) => state.givenPermission);
  const canViewGlobalReports = hasPermission({
    scopes: ["reportsnonassignedview"],
    permissions: givenPermissions,
  });

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [refreshData, setRefreshData] = useState(Date.now());
  const [category, setCategory] = useState({ ...initFilter });
  const [status, setStatus] = useState({ ...initFilter });
  const [brand, setBrand] = useState({ ...initFilter });
  const [user, setUser] = useState({ ...initFilter });
  const [role, setRole] = useState({ ...initFilter });
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    categories: [],
    status: [],
    users: [],
    roles: [],
  });
  const [tableState, setTableState] = useState({
    page: 1,
    perPage: 10,
    sortKey: "created_at",
    sortOrder: "desc",
  });
  const [isShowingAllUser, setIsShowingAllUser] = useState([]);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const fetchFilterOptions = async () => {
      try {
        const res = await getReportListFilter(undefined, cancelTokenSource.token);

        if (res.data?.data) {
          const brands = (res.data.data.brands || []).map((brandItem) => ({
            value: brandItem.id ?? brandItem.value,
            label: brandItem.brand_name ?? brandItem.label ?? brandItem.name,
            topbar_color: brandItem.topbar_color,
          }));

          setFilterOptions({
            brands: canViewGlobalReports
              ? brands
              : brands.filter((brandItem) => Number(brandItem.value) !== 0),
            categories: res.data.data.categories || [],
            status: res.data.data.status || [],
            users: (res.data.data.users || []).map((item) => ({
              value: item.value,
              label: item.label,
            })),
            roles: (res.data.data.roles || []).map((item) => ({
              value: item.value,
              label: item.label,
            })),
          });
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          setFilterOptions({
            brands: [],
            categories: [],
            status: [],
            users: [],
            roles: [],
          });
        }
      }
    };

    fetchFilterOptions();

    return () => {
      cancelTokenSource.cancel("ListOfReports filter load cancelled");
    };
  }, [canViewGlobalReports]);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    setLoading(true);

    const fetchReports = async () => {

      try {
        const params = {
          pageNo: tableState.page,
          size: tableState.perPage,
          key: tableState.sortKey,
          sort: tableState.sortOrder,
          search: search,
          viaCategory: category.arr,
          viaStatus: status.arr,
          viaBrand: brand.arr.map((brandId) => Number(brandId)),
          viaUsers: user.arr.map((userId) => Number(userId)),
          viaRoles: role.arr.map((roleId) => Number(roleId)),
          exportStatus: "false",
        };

        const res = await getReportList(params, cancelTokenSource.token);

        if (res.data?.status) {
          const responseData = res.data.data;
          let rows = getReportListRows(responseData);

          if (!canViewGlobalReports) {
            rows = rows.filter((row) => Number(row.brand_id) !== 0);
          }

          setReportData(rows);
          setTotalRows(
            Array.isArray(responseData)
              ? rows.length
              : getReportListTotal(responseData)
          );
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          setReportData([]);
          setTotalRows(0);
        }
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(fetchReports, 300);

    return () => {
      clearTimeout(delayDebounceFn);
      cancelTokenSource.cancel("ListOfReports list load cancelled");
    };
  }, [
    refreshData,
    search,
    category.arr,
    status.arr,
    brand.arr,
    user.arr,
    role.arr,
    tableState.page,
    tableState.perPage,
    tableState.sortKey,
    tableState.sortOrder,
    canViewGlobalReports,
  ]);

  const handleDelete = useCallback(async (reportId, name) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const res = await deleteReport({ id: reportId });

      if (res.data?.status) {
        setRefreshData(Date.now());
        await Swal.fire("Deleted!", `${name || "Report"} has been deleted.`, "success");
        return;
      }

      await Swal.fire(
        "Error!",
        res.data?.message || "Failed to delete report.",
        "error"
      );
    } catch (error) {
      await Swal.fire(
        "Error!",
        error?.response?.data?.message || "Failed to delete report.",
        "error"
      );
    }
  }, []);

  const exportData = useCallback(
    async (fileType, fileName) => {
      try {
        const params = {
          pageNo: tableState.page,
          size: tableState.perPage,
          key: tableState.sortKey,
          sort: tableState.sortOrder,
          search: search,
          viaCategory: category.arr,
          viaStatus: status.arr,
          viaBrand: brand.arr.map((brandId) => Number(brandId)),
          viaUsers: user.arr.map((userId) => Number(userId)),
          viaRoles: role.arr.map((roleId) => Number(roleId)),
          exportStatus: "true",
        };

        const res = await getReportList(params);
        let data = getReportListRows(res.data.data);

        if (!canViewGlobalReports) {
          data = data.filter((row) => Number(row.brand_id) !== 0);
        }

        const header = [
          "Name",
          "Category",
          "Users",
          "Role",
          "Status",
          "Brand",
          "Date Created",
          "Report URL",
        ];

        const mappedData = data.map((row) => ({
          Name: row.name || "-",
          Category: row.category_name || "-",
          Users: formatReportUsersForExport(row.users),
          Role: formatReportRolesForExport(row.roles),
          Status: row.status === 1 ? "Active" : "Draft",
          Brand: row.brand_name || "-",
          "Date Created": row.created_at
            ? moment(row.created_at).format(TABLE_DATE_FORMAT)
            : "-",
          "Report URL": row.report_url || "-",
        }));

        if (fileType === "csv") {
          const csvString = Papa.unparse({ fields: header, data: mappedData });
          const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });
          const blobURL = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.download = fileName;
          anchor.href = blobURL;
          anchor.dataset.downloadurl = ["text/csv", anchor.download, anchor.href].join(":");
          anchor.click();
          setTimeout(() => {
            URL.revokeObjectURL(blobURL);
          }, 1000);
          return;
        }

        if (fileType === "xlsx") {
          const compatibleData = mappedData.map((row) => {
            const obj = {};
            header.forEach((col) => {
              obj[col] = row[col];
            });
            return obj;
          });
          const wb = XLSX.utils.book_new();
          const ws1 = XLSX.utils.json_to_sheet(compatibleData, { header });
          XLSX.utils.book_append_sheet(wb, ws1, "React Table Data");
          XLSX.writeFile(wb, `${fileName}.xlsx`);
          return;
        }

        if (fileType === "pdf") {
          const compatibleData = mappedData.map((row) => [
            row.Name,
            row.Category,
            row.Users,
            row.Role,
            row.Status,
            row.Brand,
            row["Date Created"],
            row["Report URL"],
          ]);
          const doc = new JsPDF();
          doc.autoTable({
            head: [header],
            body: compatibleData,
            styles: {
              minCellHeight: 10,
              minCellWidth: 5,
              halign: "left",
              fontSize: 8,
            },
          });
          doc.save(`${fileName}.pdf`);
        }
      } catch (error) {
        await Swal.fire("Error!", "Failed to export data.", "error");
      }
    },
    [search, category.arr, status.arr, brand.arr, user.arr, role.arr, canViewGlobalReports, tableState]
  );

  const renderUserFilterLabel = useCallback(
    (value) => {
      const userOption = filterOptions.users.find(
        (item) => String(item.value) === String(value)
      );
      return <span className="cat">{userOption?.label || value}</span>;
    },
    [filterOptions.users]
  );

  const renderRoleFilterLabel = useCallback(
    (value) => {
      const roleOption = filterOptions.roles.find(
        (item) => String(item.value) === String(value)
      );
      return RenderUserRole(roleOption?.label || value).html;
    },
    [filterOptions.roles]
  );

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: "name",
        sortField: "name",
        sortable: true,
        cell: (row) =>
          row.name ? (
              <Link
                className="as-text-blue curser feature-name"
                title={row.name}
                to={`/reports/open/${row.id}`}
              >
                <span className="textLimit100">{row.name}</span>
              </Link>
          ) : (
            "-"
          ),
      },
      {
        name: "Category",
        selector: "category",
        sortField: "category",
        sortable: true,
        cell: (row) => (row.category_name ? RenderReportCategory(row.category_name).html : "-"),
      },
      {
        name: "Users",
        selector: "users",
        sortField: "users",
        sortable: false,
        minWidth: "220px",
        cell: (row) => (
          <UserColumn
            multipleUserStacks
            isShowingAllUser={isShowingAllUser}
            setIsShowingAllUser={setIsShowingAllUser}
            usersArray={mapReportUsersForColumn(row.users)}
            rowId={row.id}
            usersArrayData={REPORT_USER_COLUMN_FIELDS}
          />
        ),
      },
      {
        name: "Roles",
        selector: "roles",
        sortField: "roles",
        sortable: false,
        minWidth: "180px",
        cell: (row) => <ReportRolesCell roles={row.roles} />,
      },
      {
        name: "Status",
        selector: "status",
        sortField: "status",
        sortable: true,
        cell: (row) =>
          row.status != null ? RenderReportStatus(row.status).html : "-",
      },
      {
        name: "Brand",
        selector: "brand",
        sortField: "brand",
        sortable: false,
        cell: (row) => {
          if (!row.brand_name) return null;

          if (row.brand_id !== 0) {
            return (
              <Link
                to={`/administration/brandSetting/editBrand/${row.brand_id}`}
                aria-label={row.brand_name}
              >
                {row.brand_name}
              </Link>
            );
          }

          return (
            <span title={row.brand_name}>
              {row.brand_name}
            </span>
          );
        },
      },
      {
        name: "Date Created",
        selector: "created_at",
        sortField: "created_at",
        sortable: true,
        minWidth: "100px",
        width: "100px",
        cell: (row) =>
          row.created_at
            ? moment(row.created_at).format(TABLE_DATE_FORMAT)
            : "-",
      },
      {
        name: "Report URL",
        selector: "report_url",
        sortField: "report_url",
        sortable: false,
        minWidth: "120px",
        maxWidth: "180px",
        grow: 0,
        cell: (row) =>
          row.report_url ? (
            <a
              className="as-text-blue curser feature-name report-url"
              href={row.report_url}
              target="_blank"
              rel="noopener noreferrer"
              title={row.report_url}
            >
              <span className="textLimit50">{row.report_url}</span>
            </a>
          ) : (
            "-"
          ),
      },
      {
        name: "Actions",
        selector: "",
        cell: (row) => (
          <div className="assessment-08 btn-dropdown-grp">
            <div className="as-buttons">
              <Link
                className="btn btn-primary rounded-circle"
                title="View"
                to={`/reports/preview/${row.id}`}
              >
                <i className="fal fa-eye"></i>
              </Link>
              <div className="dropdown btn-dropdown-item">
                <button
                  className="btn btn-primary rounded-circle dropdown-toggle"
                  type="button"
                  id="dropdownMenuButton"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                  title="More"
                >
                  <i className="fal fa-ellipsis-h-alt"></i>
                </button>
                <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    <Link
                      className="btn btn-primary rounded-circle"
                      title="Open Report"
                      to={`/reports/open/${row.id}`}
                    >
                      <i className="fal fa-folder-open"></i>
                    </Link>
                  <PermissionsGate
                    scopes={["reportsdelete"]}
                    RenderError={() => (
                      <button
                        type="button"
                        className="btn btn-danger rounded-circle"
                        disabled
                        title="Delete"
                      >
                        <i className="fal fa-trash-alt"></i>
                      </button>
                    )}
                  >
                    <button
                      className="btn btn-danger rounded-circle"
                      title="Delete"
                      type="button"
                      onClick={() => handleDelete(row.id, row.name)}
                    >
                      <i className="fal fa-trash-alt"></i>
                    </button>
                  </PermissionsGate>
                </div>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [handleDelete, isShowingAllUser]
  );

  return (
    <DataTableComponent
      data={reportData}
      loading={loading}
      progressComponent={<SkeletonTicketList />}
      state={tableState}
      setState={setTableState}
      setSearch={setSearch}
      totalRows={totalRows}
      columns={columns}
      exportFunction={exportData}
      exportFileName="Reports_List"
      keyField="id"
      filters={[
        {
          filterName: "Category",
          optionArr: filterOptions.categories,
          state: category,
          setState: setCategory,
          renderLabelFunction: RenderReportCategory,
        },
        {
          filterName: "Status",
          optionArr: filterOptions.status,
          state: status,
          setState: setStatus,
          renderLabelFunction: RenderReportStatus,
        },
        {
          filterName: "Brand",
          optionArr: filterOptions.brands,
          state: brand,
          setState: setBrand,
          renderLabelFunction: RenderBrands,
          uniqueId: "brands",
        },
        {
          filterName: "Users",
          optionArr: filterOptions.users,
          state: user,
          setState: setUser,
          renderLabelFunction: renderUserFilterLabel,
          isHtmlInRLF: false,
          uniqueId: "users",
        },
        {
          filterName: "Role",
          optionArr: filterOptions.roles,
          state: role,
          setState: setRole,
          renderLabelFunction: renderRoleFilterLabel,
          isHtmlInRLF: false,
          uniqueId: "roles",
        },
      ]}
      tableButton={[
        <PermissionsGate
          scopes={["reportsadd"]}
          RenderError={() => (
            <button className="btn btn-primary" title="Create New" type="button" disabled>
              <i className="fal fa-plus"></i>Create New
            </button>
          )}
          key="create-report"
        >
          <Link to="/reports/create">
            <button className="btn btn-primary" title="Create New" type="button">
              <i className="fal fa-plus"></i>Create New
            </button>
          </Link>
        </PermissionsGate>,
      ]}
    />
  );
};

export default ListOfReports;
